import { MarketPlaceWhatsappMessage } from "@lib/types/MarketPlace";
import {
  Drawer,
  Descriptions,
  Skeleton,
  Typography,
  Divider,
  Image,
} from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { shortenString } from "@lib/helpers/StringHelper";
import styled from "styled-components";
import dayjs from "dayjs";
import { LanguageLabel } from "@lib/components/MiniComponents/Others";

const StyledDescriptions = styled(Descriptions)`
  th,
  td {
    padding: 5px !important;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-descriptions-item-label {
    width: 200px;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  item: MarketPlaceWhatsappMessage | null;
};

const SkeletonContent = () => (
  <StyledDescriptions bordered column={1}>
    {Array.from({ length: 7 }).map((_, index) => (
      <Descriptions.Item
        key={index}
        label={<Skeleton.Input active size="small" style={{ width: 100 }} />}
      >
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </Descriptions.Item>
    ))}
  </StyledDescriptions>
);

export const MarketPlaceWhatsappMessageDrawer = (props: Props) => {
  const { open, onClose, item } = props;
  const { messages } = useIntl();

  return (
    <StyledDrawer
      title={
        <FormattedMessage
          id={"common.previewTitle"}
          values={{
            title: messages["sidebar.marketPlace.whatsappMessages"] as string,
          }}
        />
      }
      onClose={onClose}
      open={open}
      width={600}
      id="market-place-whatsapp-message-drawer"
    >
      {!item ? (
        <SkeletonContent />
      ) : (
        <>
          <StyledDescriptions bordered column={1}>
            <Descriptions.Item label="ID">
              <Typography.Text copyable={{ text: item?.id }}>
                {shortenString(item?.id, 40)}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.createdAt" />}
            >
              {item?.createdAt
                ? dayjs(item.createdAt).format("DD.MM.YYYY HH:mm:ss")
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.createdBy" />}
            >
              {item?.createdBy || "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.updatedAt" />}
            >
              {item?.updatedAt
                ? dayjs(item.updatedAt).format("DD.MM.YYYY HH:mm:ss")
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.updatedBy" />}
            >
              {item?.updatedBy || "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.language" />}
            >
              {item?.language ? (
                <LanguageLabel languageCode={item.language} />
              ) : (
                "-"
              )}
            </Descriptions.Item>
          </StyledDescriptions>

          <Divider />

          <div style={{ marginTop: 16 }}>
            <Typography.Title level={5} style={{ marginBottom: 12 }}>
              <FormattedMessage id="common.message" />
            </Typography.Title>

            <Typography.Text>{item?.message || "-"}</Typography.Text>
          </div>

          {item?.imageUrl && (
            <>
              <Divider />
              <div style={{ marginTop: 16 }}>
                <Typography.Title level={5} style={{ marginBottom: 12 }}>
                  <FormattedMessage id="common.image" />
                </Typography.Title>

                <Image
                  src={item.imageUrl}
                  alt="WhatsApp Message Image"
                  style={{ maxWidth: "100%" }}
                />
              </div>
            </>
          )}
        </>
      )}
    </StyledDrawer>
  );
};
